import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, LogOut, Send, AlertCircle, CheckCircle2, Loader2, Bell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin dashboard for the Evolution-API-backed WhatsApp integration.
 * Shows live connection state, the pairing QR code while disconnected,
 * a manual "send test message" form, and one-click actions to register
 * the inbound webhook, restart, or log out the WA instance.
 */
const WhatsAppAdmin = () => {
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMsg, setTestMsg] = useState('Test depuis HorseGO 👋');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/whatsapp/status`);
      setStatus(data);
      // If not yet connected, fetch the QR code.
      const state = (data?.state || '').toLowerCase();
      if (data?.enabled && state !== 'open') {
        try {
          const { data: q } = await axios.get(`${API}/whatsapp/qr`);
          setQr(q || null);
        } catch {
          setQr(null);
        }
      } else {
        setQr(null);
      }
    } catch (err) {
      setToast({ kind: 'error', text: err?.response?.data?.detail || 'Status fetch failed' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      await fetchStatus();
      if (!cancelled) timer = setTimeout(tick, 8000);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [fetchStatus]);

  const callAction = async (label, fn) => {
    setBusy(label);
    setToast(null);
    try {
      const data = await fn();
      setToast({ kind: 'ok', text: `${label} ✓` });
      await fetchStatus();
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed';
      setToast({ kind: 'error', text: `${label}: ${detail}` });
    } finally {
      setBusy('');
    }
  };

  const onSetupWebhook = () =>
    callAction('Register webhook', () => axios.post(`${API}/whatsapp/setup-webhook`, {
      public_url: process.env.REACT_APP_BACKEND_URL || window.location.origin,
    }));
  const onRestart = () => callAction('Restart instance', () => axios.post(`${API}/whatsapp/restart`));
  const onLogout = () => {
    if (!window.confirm('Log out the WhatsApp instance? You will need to scan a new QR.')) return;
    return callAction('Logout', () => axios.post(`${API}/whatsapp/logout`));
  };
  const onSendTest = (e) => {
    e?.preventDefault?.();
    if (!testPhone || !testMsg) return;
    return callAction('Send test', () => axios.post(`${API}/whatsapp/send`, { phone: testPhone, text: testMsg }));
  };
  const onRunReminders = () =>
    callAction('Run pickup reminders', () => axios.post(`${API}/whatsapp/run-pickup-reminders`));

  const state = (status?.state || '').toLowerCase();
  const connected = status?.enabled && state === 'open';
  const enabled = !!status?.enabled;

  // WAHA returns a flat {base64} body. Pairing-code path is reserved for
  // versions that expose it; left here for forward compatibility.
  const qrBase64 = qr?.base64 || null;
  const pairingCode = qr?.pairingCode || qr?.code || null;

  return (
    <div data-testid="admin-whatsapp-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">WhatsApp Bot</h2>
        <button
          type="button"
          onClick={fetchStatus}
          disabled={loading}
          data-testid="wa-refresh-btn"
          className="bg-white/5 hover:bg-white/10 text-gray-200 px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {/* Not configured banner */}
      {!enabled && (
        <div data-testid="wa-disabled-banner" className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200 space-y-1">
            <div className="font-semibold">WhatsApp / WAHA not configured.</div>
            <p className="text-amber-200/80">
              Set <code className="text-xs bg-black/40 px-1 rounded">WAHA_API_URL</code> and{' '}
              <code className="text-xs bg-black/40 px-1 rounded">WAHA_API_KEY</code> in{' '}
              <code className="text-xs bg-black/40 px-1 rounded">/app/backend/.env</code>, then restart the backend.
              Optionally set <code className="text-xs bg-black/40 px-1 rounded">WAHA_SESSION</code> (default <code className="text-xs bg-black/40 px-1 rounded">default</code>),{' '}
              <code className="text-xs bg-black/40 px-1 rounded">WAHA_WEBHOOK_TOKEN</code> to verify inbound webhook calls,
              and <code className="text-xs bg-black/40 px-1 rounded">WAHA_PUBLIC_BACKEND_URL</code> so the &ldquo;Register webhook&rdquo; button knows where WAHA should call.
            </p>
          </div>
        </div>
      )}

      {/* Status card */}
      {enabled && (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-5 grid md:grid-cols-2 gap-5">
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">Session</div>
            <div className="text-amber-400 font-mono mt-1" data-testid="wa-instance-name">{status.session}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wide mt-4">Connection</div>
            <div className="flex items-center gap-2 mt-1" data-testid="wa-connection-state">
              {connected
                ? <><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400 font-semibold">Connected</span></>
                : <><span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /><span className="text-amber-400">{state || 'unknown'}</span></>}
            </div>
          </div>

          <div className="flex flex-col gap-2 md:items-end">
            <button
              type="button"
              onClick={onSetupWebhook}
              disabled={busy === 'Register webhook'}
              data-testid="wa-setup-webhook-btn"
              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50"
            >
              {busy === 'Register webhook' ? 'Registering…' : 'Register webhook URL'}
            </button>
            <button
              type="button"
              onClick={onRestart}
              disabled={busy === 'Restart instance'}
              data-testid="wa-restart-btn"
              className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {busy === 'Restart instance' ? 'Restarting…' : 'Restart instance'}
            </button>
            <button
              type="button"
              onClick={onLogout}
              disabled={busy === 'Logout'}
              data-testid="wa-logout-btn"
              className="bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              {busy === 'Logout' ? 'Logging out…' : 'Logout instance'}
            </button>
            <button
              type="button"
              onClick={onRunReminders}
              disabled={busy === 'Run pickup reminders'}
              data-testid="wa-run-reminders-btn"
              className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30 rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <Bell className="w-3.5 h-3.5" />
              {busy === 'Run pickup reminders' ? 'Sending…' : 'Run 24h pickup reminders now'}
            </button>
          </div>
        </div>
      )}

      {/* QR / pairing */}
      {enabled && !connected && (qrBase64 || pairingCode) && (
        <div data-testid="wa-qr-panel" className="bg-[#1a1a1a] rounded-xl border border-white/10 p-5">
          <h3 className="text-white font-semibold mb-3">Pair your WhatsApp number</h3>
          <p className="text-sm text-gray-400 mb-4">
              Open WhatsApp on the phone you want to use → <strong>Settings → Linked Devices → Link a Device</strong>, then scan the QR below.
          </p>
          <div className="flex flex-col md:flex-row gap-5 items-center">
            {qrBase64 && (
              <img
                src={qrBase64.startsWith('data:') ? qrBase64 : `data:image/png;base64,${qrBase64}`}
                alt="WhatsApp pairing QR"
                className="w-56 h-56 bg-white rounded-lg p-2"
                data-testid="wa-qr-image"
              />
            )}
            {pairingCode && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Or pairing code</div>
                <div className="text-3xl font-mono text-amber-400 tracking-widest mt-2" data-testid="wa-pairing-code">
                  {pairingCode}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Send test */}
      {enabled && (
        <form
          onSubmit={onSendTest}
          className="bg-[#1a1a1a] rounded-xl border border-white/10 p-5 space-y-3"
          data-testid="wa-send-test-form"
        >
          <h3 className="text-white font-semibold">Send a test message</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <input
              type="tel"
              required
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+212600000000"
              data-testid="wa-test-phone"
              className="bg-white/5 border border-white/10 focus:border-amber-500/50 rounded-lg px-3 py-2 text-white text-sm"
            />
            <input
              type="text"
              required
              value={testMsg}
              onChange={(e) => setTestMsg(e.target.value)}
              placeholder="Message"
              data-testid="wa-test-msg"
              className="bg-white/5 border border-white/10 focus:border-amber-500/50 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={busy === 'Send test' || !connected}
            data-testid="wa-send-test-btn"
            className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2 transition-colors"
          >
            <Send className="w-4 h-4" />
            {busy === 'Send test' ? 'Sending…' : connected ? 'Send' : 'Connect first to send'}
          </button>
        </form>
      )}

      {toast && (
        <div
          data-testid="wa-toast"
          className={`rounded-lg px-3 py-2 text-sm ${
            toast.kind === 'ok'
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300'
              : 'bg-red-500/10 border border-red-500/30 text-red-300'
          }`}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default WhatsAppAdmin;
